import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const carouselImages = [
    {
      src: '/assets/images/team_collaboration_carousel_one.png',
      alt: 'Professional team collaboration scene showing diverse business professionals in modern workspace giving presentation with charts and graphs on screen, team members engaged in discussion, warm office lighting, business attire, professional atmosphere with computers and documents'
    },
    {
      src: '/assets/images/team_collaboration_carousel_two.png',
      alt: 'Business team collaboration meeting with professionals reviewing documents and presentation slides on large monitor, diverse team members in conference room setting, professional attire, engaging discussion atmosphere, modern office environment with laptops and charts'
    },
    {
      src: '/assets/images/team_collaboration_carousel_three.png',
      alt: 'Professional team workshop scene with business people collaborating around whiteboard with diagrams and flowcharts, team members pointing and discussing strategy, modern corporate setting with glass walls, natural lighting, professional business attire and collaborative atmosphere'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages?.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [carouselImages?.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages?.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages?.length) % carouselImages?.length);
  };

  return (
    <section className="relative h-screen overflow-hidden">
      {/* Carousel Background */}
      <div className="absolute inset-0">
        {carouselImages?.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <Image
              src={image?.src}
              alt={image?.alt}
              className="w-full h-full object-cover"
            />
            {/* CRF Perfection Brand Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#96121D]/80 via-black/70 to-[#9B8E57]/60"></div>
          </div>
        ))}
      </div>

      {/* Carousel Navigation */}
      <button
        onClick={prevSlide}
        className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-[#96121D]/80 transition-all duration-300 z-10 border border-[#9B8E57]/30"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-[#96121D]/80 transition-all duration-300 z-10 border border-[#9B8E57]/30"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
        </svg>
      </button>

      {/* Content Overlay */}
      <div className="relative z-10 flex items-center justify-center h-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center px-6 py-3 rounded-full bg-black/40 backdrop-blur-md border border-[#9B8E57]/40 mb-8">
              <span className="w-3 h-3 bg-gradient-to-r from-[#96121D] to-[#9B8E57] rounded-full mr-3 animate-pulse"></span>
              <span className="text-white/90 text-base font-semibold">Centre d'Excellence & Innovation</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Blog <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#9B8E57] to-amber-400">CRF PERFECTION</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#96121D] to-red-600">Expertise & Innovation</span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-4xl mx-auto leading-relaxed font-medium">
              Découvrez nos insights d'experts en Intelligence Artificielle, transformation digitale, 
              et développement des compétences pour propulser votre organisation vers l'excellence.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto mb-12">
              <div className="text-center bg-black/40 backdrop-blur-md rounded-xl p-6 border border-[#9B8E57]/30">
                <div className="text-4xl font-bold text-[#9B8E57] mb-3">50+</div>
                <div className="text-white/80 text-lg">Articles d'Expertise</div>
              </div>
              <div className="text-center bg-black/40 backdrop-blur-md rounded-xl p-6 border border-[#96121D]/30">
                <div className="text-4xl font-bold text-[#96121D] mb-3">12</div>
                <div className="text-white/80 text-lg">Catégories Spécialisées</div>
              </div>
              <div className="text-center bg-black/40 backdrop-blur-md rounded-xl p-6 border border-[#9B8E57]/30">
                <div className="text-4xl font-bold text-amber-400 mb-3">2500+</div>
                <div className="text-white/80 text-lg">Professionnels Engagés</div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="#articles" className="px-10 py-4 bg-gradient-to-r from-[#96121D] to-[#9B8E57] text-white font-bold rounded-xl hover:from-[#96121D]/90 hover:to-[#9B8E57]/90 transition-all duration-300 shadow-xl hover:shadow-2xl text-lg">
                Explorer les Articles
              </a>
              <a href="#newsletter" className="px-10 py-4 bg-black/30 backdrop-blur-md border-2 border-[#9B8E57]/60 text-white font-bold rounded-xl hover:bg-black/40 hover:border-[#9B8E57] transition-all duration-300 text-lg">
                S'abonner à la Newsletter
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Carousel Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3 z-10">
        {carouselImages?.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-3 rounded-full transition-all duration-300 ${
              index === currentSlide 
                ? 'bg-[#9B8E57] w-8' 
                : 'bg-white/40 hover:bg-white/60 w-3'
            }`}
          />
        ))}
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-10 w-24 h-24 bg-gradient-to-r from-[#96121D]/20 to-[#9B8E57]/20 rounded-full blur-2xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-10 w-32 h-32 bg-gradient-to-r from-[#9B8E57]/20 to-amber-600/20 rounded-full blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>
    </section>
  );
};

export default HeroSection;